import { SkyMessageBar } from "@skypointcloud/skypoint-ui";
import type { RunStepRow } from "@shared/types/runs";

/**
 * Round 4 item 7a: "Ran as a full import", on the run page.
 *
 * An incremental connection reads only what changed since its last import, so its first import,
 * and an import of tables none of which has landed yet, reads everything: the runner records why
 * on the extract step (`metrics.refreshFallback`, `runner/config/bundle.ts` `refreshPlanFor`) and
 * logs `REFRESH_FELL_BACK_TO_FULL`. Until now only the events log said so, and a first import that
 * took an hour looked like incremental had failed. The reasons are the runner's closed words; an
 * unknown one still says the import read everything, without guessing why.
 */

/** Round 6 (owner, 2026-10-01 23:40, binding): the first incremental run's own words. */
const FIRST_INCREMENTAL_RUN =
  "Imported everything: this connection had no previous import. The next import continues from this one.";

const REASONS: Readonly<Record<string, string>> = {
  no_successful_load: FIRST_INCREMENTAL_RUN,
  // #2521: a confirmed destination move cleared every bound of a connection that had imported. A
  // scoped or failed run says the same, so the words claim neither every table nor what follows.
  destination_reset:
    "The destination moved, so this import read its tables in full into the new place.",
  no_bound_in_scope:
    "Incremental was chosen, but each table in this import had no earlier import or was asked to re-sync, so those tables were read in full.",
};
const UNKNOWN_REASON = "Incremental was chosen, but this import read everything.";

function fallbackOf(metrics: unknown): string | null {
  if (!metrics || typeof metrics !== "object") return null;
  const value = (metrics as { refreshFallback?: unknown }).refreshFallback;
  return typeof value === "string" && value !== "" ? value : null;
}

/**
 * One sentence per connection whose extract fell back, in step order; a run of one connection
 * says it plainly, a run of several prefixes each with the connection's name.
 */
export function refreshFallbackLines(steps: readonly RunStepRow[]): string[] {
  const extracts = steps.filter((row) => row.step === "extract");
  const connections = new Set(extracts.map((row) => row.dataflowId ?? row.id));
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const row of extracts) {
    const reason = fallbackOf(row.metrics);
    const key = row.dataflowId ?? row.id;
    if (!reason || seen.has(key)) continue;
    seen.add(key);
    const sentence = REASONS[reason] ?? UNKNOWN_REASON;
    lines.push(
      connections.size > 1 && row.dataflowName ? `${row.dataflowName}: ${sentence}` : sentence,
    );
  }
  return lines;
}

export function RefreshFallbackNote({ steps }: { steps: readonly RunStepRow[] }) {
  const lines = refreshFallbackLines(steps);
  if (lines.length === 0) return null;
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      title="Ran as a full import"
      message={lines.join(" ")}
      testId="run-refresh-fallback"
    />
  );
}
